import IonIcon from '@reacticons/ionicons'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { memo } from 'react'
import Modal from 'react-modal'
import { premiumPlanFeatures } from 'src/constants/constants'
import { useAuthenticated } from 'src/hooks/useAuthenticated'

interface PremiumModalProps {
  isOpen: boolean
  origin?: string
  onClose: () => void
  fromPage?: string
}

const ModalStyles: Modal.Styles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    padding: '0px',
    transform: 'translate(-50%, -50%)',
    borderWidth: '0px',
    backgroundColor: 'transparent',
    border: '0px solid red',
    // Enhanced mobile responsiveness
    maxWidth: '95vw',
    maxHeight: '97vh',
    minWidth: '320px', // Ensure minimum width for very small screens
    overflow: 'hidden',
  },
  overlay: {
    position: 'fixed', // Use fixed for better mobile support
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: '9999', // High z-index to ensure it's above everything
    backgroundColor: 'rgba(0, 0, 0, 0.5)', // Semi-transparent background
  },
}

export const PremiumModal: React.FC<PremiumModalProps> = memo(({ isOpen, onClose, fromPage = '' }) => {
  const router = useRouter()

  const handleClose = () => {
    if (fromPage === 'smartsearch') {
      router.push('/')
    } else {
      onClose()
    }
  }

  const { status } = useAuthenticated()
  const moreInfo = 'https://hub.legaltechnologyhub.com/more-info'

  return (
    <Modal
      closeTimeoutMS={300} // Increased for smoother animation
      isOpen={isOpen}
      onRequestClose={handleClose}
      style={ModalStyles}
      ariaHideApp={false}
      shouldCloseOnOverlayClick={false} // Prevent accidental closing
      shouldCloseOnEsc={true} // Allow ESC key to close
    >
      <div className="z-50 flex flex-col md:w-[610px] w-[95vw] md:pl-0 md:pr-0 pl-[10px] pr-[10px] max-h-[97vh] overflow-y-auto">
        <div className="flex flex-row w-full z-70">
          <div className="ml-auto cursor-pointer text-textdark relative" onClick={handleClose}>
            <IonIcon
              name="close-circle"
              className="!h-[30px] !w-[30px] relative z-990"
              style={{
                top: '0px',
                right: '0px',
                position: 'relative',
                zIndex: '999',
                color: '#FFF',
              }}
            />
          </div>
        </div>

        <div className={`flex flex-col overflow-y-hidden mt-[-25px] mr-[15px] z-50`}>
          <div className="bg-white rounded-[5px] flex flex-row w-full z-50">
            <div className="flex flex-col w-full z-50">
              <div className="h-2 rounded-t-[5px] bg-lightRoyalBlue"></div>
              <div className="flex flex-col p-6 md:p-12 text-[1px] md:text-[16px] font-primary text-darkgray">
                <div className="flex flex-wrap my-[10px] md:my-[5px]">
                  <p className="text-[22px] sm:text-[26px] md:text-[30px] font-bold font-secondary text-textdark text-left leading-tight">
                    Upgrade to <b className="text-[#332aef]"> premium</b> to gain access to this feature
                  </p>
                </div>

                {premiumPlanFeatures.map((option: string, index: number) => (
                  <div key={index} className="flex flex-row mt-[8px] md:mt-[10px]">
                    <IonIcon
                      name="checkmark-circle-outline"
                      className="!w-[20px] md:!w-[25px] ![h-20px] md:![h-25px] mr-[8px] md:mr-[12px] text-[#332aef] flex-shrink-0"
                    />
                    <p className="text-textdark font-bold text-[13px] md:text-[16px] leading-relaxed">{option}</p>
                  </div>
                ))}
                <div>
                  <p className="text-white font-secondary text-[12px] md:text-[14px] font-bold text-center rounded-[22.5px] px-[20px] md:px-[25px] bg-navy w-full md:w-[206px] h-[40px] md:h-[45px] my-[20px] md:my-[25px] items-center justify-center flex">
                    <Link href={moreInfo} target="_blank" onClick={handleClose}>
                      Sign Up for Premium
                    </Link>
                  </p>
                </div>
                {status !== 'authenticated' && (
                  <div className="flex mt-[20px] md:mt-[10px] mb-[10px] md:mb-[15px] text-[13px] md:text-[16px] flex-wrap">
                    Have an account?
                    <p className="font-bold font-primary text-textdark underline ml-1">
                      <Link href={{ pathname: '/signin' }}>Sign in</Link>
                    </p>
                  </div>
                )}
                <div className="flex mb-[10px] md:mb-[12px] text-[13px] md:text-[16px] flex-wrap">
                  Are you a vendor?
                  <p className="font-bold font-primary text-textdark underline ml-1">
                    <Link href="/lth-solution/for-vendors">Vendor Information</Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
})
